import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { applySiteMeta } from "../lib/siteMeta";
import Layout from "../components/Layout";
import {
  CircleNotch, UploadSimple, FloppyDisk, CheckCircle, Globe, MagnifyingGlass,
} from "@phosphor-icons/react";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-neutral-700">{label}</label>
      {children}
      {hint ? <p className="mt-1.5 text-xs text-neutral-400">{hint}</p> : null}
    </div>
  );
}

function ImageField({ label, hint, value, onChange, accept = "image/*", maxMB = 1, testid }) {
  const ref = useRef(null);
  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please choose an image file."); return; }
    if (file.size > maxMB * 1024 * 1024) { alert(`Image must be under ${maxMB}MB.`); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-4">
        {value ? (
          <img src={value} alt="" className="h-14 w-14 rounded-sm border border-neutral-200 object-contain" data-testid={`${testid}-thumb`} />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-sm border border-dashed border-neutral-300 text-neutral-300"><UploadSimple size={18} /></div>
        )}
        <div className="flex items-center gap-3">
          <button type="button" data-testid={`${testid}-upload`} onClick={() => ref.current?.click()}
            className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
            <UploadSimple size={15} weight="bold" />{value ? "Replace" : "Upload"}
          </button>
          {value ? <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange("")} className="text-sm font-medium text-neutral-500 hover:text-[#DC2626]">Remove</button> : null}
          <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} />
        </div>
      </div>
    </Field>
  );
}

export default function SiteSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => { api.get("/site-settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { title, description, keywords, favicon, og_image, theme_color } = s;
      const r = await api.put("/site-settings", { title, description, keywords, favicon, og_image, theme_color });
      setS(r.data);
      applySiteMeta(r.data); // reflect immediately in this tab
      setFlash(true); setTimeout(() => setFlash(false), 2200);
    } finally { setSaving(false); }
  };

  if (!s) return <Layout><div className="flex h-60 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;

  const host = (process.env.REACT_APP_BACKEND_URL || "https://asset-hub.example.com").replace(/^https?:\/\//, "");

  return (
    <Layout>
      <div data-testid="site-settings-page">
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Site control</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Site Settings &amp; SEO</h1>
        <p className="mt-2 max-w-3xl text-base text-neutral-600">
          Control how Asset Hub appears in browsers, search engines and when shared on social. These details drive the page title, favicon, meta description and social preview card.
        </p>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {/* Editor */}
          <div className="space-y-5 rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
            <Field label="Page title" hint="Shown in the browser tab and as the headline in search results.">
              <input data-testid="seo-field-title" value={s.title || ""} onChange={(e) => set("title", e.target.value)}
                className="w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-base outline-none focus:border-[#0055FF]" />
            </Field>
            <Field label="Meta description" hint={`${(s.description || "").length}/160 characters · the snippet shown under your title in Google.`}>
              <textarea data-testid="seo-field-description" rows={3} value={s.description || ""} onChange={(e) => set("description", e.target.value)}
                className="w-full resize-none rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-base outline-none focus:border-[#0055FF]" />
            </Field>
            <Field label="Meta keywords" hint="Comma-separated keywords (optional).">
              <input data-testid="seo-field-keywords" value={s.keywords || ""} onChange={(e) => set("keywords", e.target.value)}
                className="w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-base outline-none focus:border-[#0055FF]" />
            </Field>
            <ImageField label="Favicon" hint="The small icon in the browser tab. Square PNG/ICO/SVG recommended · max 1MB." value={s.favicon} onChange={(v) => set("favicon", v)} maxMB={1} testid="seo-favicon" />
            <ImageField label="Social share image (Open Graph)" hint="Shown when a link is shared on social / chat. 1200×630 recommended · max 3MB." value={s.og_image} onChange={(v) => set("og_image", v)} maxMB={3} testid="seo-og" />
            <Field label="Theme colour" hint="Used by mobile browsers for the address-bar tint.">
              <div className="flex items-center gap-3">
                <input type="color" data-testid="seo-field-theme-color" value={s.theme_color || "#0055FF"} onChange={(e) => set("theme_color", e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded-sm border border-neutral-300 bg-white p-1" />
                <input value={s.theme_color || ""} onChange={(e) => set("theme_color", e.target.value)}
                  className="w-32 rounded-sm border border-neutral-300 bg-white px-3 py-2.5 font-mono text-sm outline-none focus:border-[#0055FF]" />
              </div>
            </Field>

            <div className="flex items-center justify-between border-t border-neutral-200 pt-4">
              <p className="text-xs text-neutral-400">
                {s.updated_at ? `Last saved ${new Date(s.updated_at).toLocaleString()} by ${s.updated_by || "admin"}` : "Not saved yet"}
              </p>
              <button data-testid="seo-save-btn" onClick={save} disabled={saving}
                className="inline-flex items-center gap-2 rounded-sm bg-[#059669] px-4 py-2 text-sm font-semibold text-white hover:bg-[#047857] disabled:opacity-60">
                {saving ? <CircleNotch size={15} className="animate-spin" /> : flash ? <CheckCircle size={15} weight="fill" /> : <FloppyDisk size={15} weight="bold" />}
                {flash ? "Saved" : "Save changes"}
              </button>
            </div>
          </div>

          {/* Previews */}
          <div className="space-y-6">
            {/* Browser tab */}
            <div className="rounded-sm border border-neutral-200 bg-white shadow-sm">
              <div className="border-b border-neutral-200 px-5 py-3"><span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Browser tab</span></div>
              <div className="p-5">
                <div className="inline-flex max-w-full items-center gap-2 rounded-t-md border border-b-0 border-neutral-200 bg-neutral-100 px-3 py-2" data-testid="seo-tab-preview">
                  {s.favicon ? <img src={s.favicon} alt="" className="h-4 w-4 rounded-sm object-contain" /> : <Globe size={16} className="text-neutral-400" />}
                  <span className="truncate text-sm text-neutral-700">{s.title || "Untitled"}</span>
                </div>
              </div>
            </div>

            {/* Google result */}
            <div className="rounded-sm border border-neutral-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
                <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Search result</span>
                <MagnifyingGlass size={14} className="text-neutral-400" />
              </div>
              <div className="p-5" data-testid="seo-search-preview">
                <p className="text-xs text-neutral-600">{host}</p>
                <p className="mt-0.5 text-lg text-[#1a0dab] hover:underline">{s.title || "Untitled"}</p>
                <p className="mt-0.5 text-sm leading-snug text-neutral-600">{(s.description || "No description set.").slice(0, 160)}</p>
              </div>
            </div>

            {/* Social card */}
            <div className="rounded-sm border border-neutral-200 bg-white shadow-sm">
              <div className="border-b border-neutral-200 px-5 py-3"><span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Social share card</span></div>
              <div className="p-5">
                <div className="overflow-hidden rounded-md border border-neutral-200" data-testid="seo-social-preview">
                  <div className="aspect-[1200/630] w-full bg-neutral-100">
                    {s.og_image ? <img src={s.og_image} alt="" className="h-full w-full object-cover" /> : (
                      <div className="flex h-full w-full items-center justify-center text-sm text-neutral-400">No social image set</div>
                    )}
                  </div>
                  <div className="bg-neutral-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-neutral-400">{host}</p>
                    <p className="truncate text-sm font-semibold text-neutral-800">{s.title || "Untitled"}</p>
                    <p className="truncate text-xs text-neutral-500">{s.description || ""}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
